import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik, FormikHelpers} from 'formik';
import {useDispatch} from "react-redux";
import classnames from 'classnames';
import FilterText from '../../../_common/components/FilterText';
import {usePriceCatalogContext} from '../../contexts/PriceCatalogContext';
import {applyFilter} from '../../../../../utils/prepareApplyFilter';
import {getListForExcel, uploadFile} from "../../actions/priceCatalogActions";
import {generateExcelFile} from "../../../_common/utils/excel";
import {columnNamesPriceCatalog} from "../../../_common/constants/excelColumnNames";
import {toAbsoluteUrl} from "../../../../../_metronic/_helpers";
import ButtonAction from "../../../_common/components/ButtonAction";
import {PriceCatalogItem} from "../../types";
import {MergeCell, ExcelCellValue, PreparedExcelRow, WorkSheet} from "../../../_common/types";
import PopupAddEditItem from "../PopupAddEditItem/PopupAddEditItem";
import {AddEditItemProvider} from "../../contexts/AddEditItemContext";
import ButtonUploadFile from "../../../_common/components/ButtonUploadFile";
import AnimatedUploadingFilePopUp from "../../../_common/components/AnimatedUploadingFilePopUp";
import {showGlobalMessage} from "../../../_common/components/GlobalMessage/actions/showGlobalMessage";
import {messages as M} from "../../../../../_metronic/i18n/messages";
import styles from '../../../_common/styles/filtersBar.module.scss';

type PriceCatalogFilterValues = {
  mainCategory: string;
  secondCategory: string;
  serviceDescription: string;
};

export function PriceCatalogFilters() {
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  const context = usePriceCatalogContext();
  const catalogProps = useMemo(() => {
    return {
      queryParams: context.queryParams,
      setQueryParams: context.setQueryParams,
    };
  }, [context]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const prefix = 'filter-placeholder';

  const qs = new URLSearchParams(window.location.search);

  const initialValues: PriceCatalogFilterValues = {
    mainCategory: qs.get('mainCategory') || '',
    secondCategory: qs.get('secondCategory') || '',
    serviceDescription: qs.get('serviceDescription') || '',
  };

  const handleExport = (): void => {
    const merge: MergeCell[] = [{s: {r: 1, c: 7}, e: {r: 1, c: 12}}];

    const customCell = (ws: WorkSheet): ExcelCellValue => {
      return (ws['H2'] = {
        t: 's',
        v: 'פרטי איש קשר',
        s: {
          alignment: {horizontal: 'center'},
          font: {bold: true},
        },
      });
    };

    setLoading(true);
    dispatch(getListForExcel(catalogProps.queryParams))
      // @ts-ignore
      .then(prepareDataForExcel)
      .then((data: PreparedExcelRow[]) => {
        generateExcelFile(data, 'Price catalog.xlsx', 'קטלוג מחירים', merge, customCell);
      })
      .catch((error: unknown) => {
        console.error(`Error: + ${error}`);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = (data: PriceCatalogItem[]): PreparedExcelRow[] => {
    return data.map((item, index) => {
      const {
        id,
        mainCategory,
        secondCategory,
        serviceDescription,
        servicePrice,
        notes,
      } = item as PriceCatalogItem;

      return {
        [columnNamesPriceCatalog[0]]: index + 1,
        [columnNamesPriceCatalog[1]]: id,
        [columnNamesPriceCatalog[2]]: mainCategory,
        [columnNamesPriceCatalog[3]]: secondCategory,
        [columnNamesPriceCatalog[4]]: serviceDescription,
        [columnNamesPriceCatalog[5]]: servicePrice,
        [columnNamesPriceCatalog[6]]: notes,
      };
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadFile(file, file.name);
      if (result?.data?.errorCode < 0) {
        const numberOfFailedRows = Array.isArray(result?.data?.body?.results) && result?.data?.body?.results
          .filter((item: { status: string; }) => item?.status === 'error')
          .length;
        const message = numberOfFailedRows
          ? [numberOfFailedRows, 'שורות לא עודכנו, למעט שדה הערות כל שאר השדות הם חובה'].join(' ')
          : M['-1']
        ;
        dispatch(showGlobalMessage('error', message))
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTimeout(() => {
        setUploading(false);
      }, 2000)
      input.value = '';
      await context.updateTable();
    }
  };

  return (
    <>
      <AnimatedUploadingFilePopUp show={uploading}/>
      <Formik<PriceCatalogFilterValues>
        initialValues={initialValues}
        onSubmit={(values: PriceCatalogFilterValues, _: FormikHelpers<PriceCatalogFilterValues>) => applyFilter(values, catalogProps)}
        enableReinitialize
      >
        {({values, handleSubmit, handleBlur, setFieldValue}) => (
          <form onSubmit={handleSubmit} className={styles.container}>
            <div className={classnames(styles.filtersContainer, 'justify-content-start')}>
              <FilterText
                fieldName="mainCategory"
                value={values.mainCategory}
                placeholder={formatMessage({id: `${prefix}-mainCategory`, defaultMessage: 'Main',})}
                containerClassnames={styles.filter}
                setFieldValue={setFieldValue}
                handleSubmit={handleSubmit}
                handleBlur={handleBlur}
                formatValue={false}
              />
              <FilterText
                fieldName="secondCategory"
                value={values.secondCategory}
                placeholder={formatMessage({id: `${prefix}-secondCategory`, defaultMessage: 'Second',})}
                containerClassnames={styles.filter}
                setFieldValue={setFieldValue}
                handleSubmit={handleSubmit}
                handleBlur={handleBlur}
                formatValue={false}
              />
              <FilterText
                fieldName="serviceDescription"
                value={values.serviceDescription}
                placeholder={formatMessage({id: `${prefix}-serviceDescription`, defaultMessage: 'Description'})}
                containerClassnames={styles.filter}
                setFieldValue={setFieldValue}
                handleSubmit={handleSubmit}
                handleBlur={handleBlur}
                formatValue={false}
              />
            </div>
            <div className={styles.buttonsContainer}>
              <ButtonUploadFile
                variant={'warning'}
                label={
                  <div className={'px-4 cursor-pointer'}>
                    <img src={toAbsoluteUrl('/media/common/Upload_white.svg')} alt="i"/>
                    <span>{formatMessage({id: 'price-catalog-page-upload-file', defaultMessage: 'Upload file'})}</span>
                  </div>
                }
                onChange={handleFileChange}
                accept={'.xlsx'}
                customContainerStyles={'mx-4'}
              />
              <AddEditItemProvider id={-1}>
                <PopupAddEditItem/>
              </AddEditItemProvider>
              <ButtonAction
                variant={'success'}
                label={
                  <>
                    {loading
                      ? (
                        <div className="spinner-border spinner-border-md m-auto text-light" role="status"/>
                      )
                      : (
                        <>
                          <img
                            src={toAbsoluteUrl('/media/common/Excel_white.svg')}
                            alt="export" className={'mr-2'}
                          />
                          <span>{formatMessage({id: 'button-export-to-excel', defaultMessage: 'Export'})}</span>
                        </>
                      )
                    }
                  </>
                }
                onClick={handleExport}
                customClassnames={'ml-4 w-100px'}
              />
            </div>
          </form>
        )}
      </Formik>
    </>
  );
}

export default PriceCatalogFilters;